// src/components/AnimationView/engine/AssetBuilder.ts
import type { TScene, TObstacle } from "../../../models/objects/objects";
import type { TSprite } from "../../../models/objects/sprite";
import type { CharacterConfig, ObjectConfig } from "../types/config";
import type { TKeyValueDataString } from "../../../models/objects/gen";

const normKey = (s?: string) => String(s ?? "").trim().replace(/\s+/g, "_");
const isNum = (k: string) => /^\d+$/.test(k);

function pickUrl(v: any): string | undefined {
    return (
        v?.assets?.texture ||
        v?.assets?.background ||
        v?.sprite?.url ||
        v?.image_url ||
        v?.texture ||
        v?.background ||
        v?.url ||
        v?.img ||
        ""
    ) || undefined;
}

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────
export function buildImageAssets(input: {
    scenes: TScene[];
    sprites: TSprite[];
    obstacles: TObstacle[];
    characters?: CharacterConfig[];
    objects?: ObjectConfig[];
}): TKeyValueDataString[] {
    const out: TKeyValueDataString[] = [];
    const seen = new Set<string>();

    const push = (key: string, value: string) => {
        const k = normKey(key);
        if (!k || !value) return;
        if (seen.has(k)) return;
        seen.add(k);
        out.push({ key: k, value });
    };

    // 1) Scenes → "scene_<id>"
    for (const s of input.scenes ?? []) {
        const id = String(s?.id ?? "").trim();
        if (!id) continue;
        const url = pickUrl(s);
        if (!url) continue;
        push(`scene_${id}`, url);
    }

    // 2) Sprites catalogue → "sprite_<id>"
    for (const sp of input.sprites ?? []) {
        const id = String((sp as any)?.id ?? "").trim();
        if (!id) continue;
        const url = pickUrl(sp);
        if (!url) continue;
        push(`sprite_${id}`, url);
    }

    // 3) Obstacles catalogue → "obstacle_<id>"
    for (const ob of input.obstacles ?? []) {
        const id = String((ob as any)?.id ?? "").trim();
        if (!id) continue;
        const url = pickUrl(ob);
        if (!url) continue;
        push(`obstacle_${id}`, url);
    }

    // 4) Characters → publier SOUS LA CLÉ DIRECTE idPhaser + alias "char_<idPhaser>"
    for (const ch of input.characters ?? []) {
        const k =
            normKey((ch as any)?.idPhaser ?? (ch as any)?.id ?? (ch as any)?.name ?? "");
        if (!k) continue;
        const url = pickUrl(ch);
        if (!url) continue;

        // clé directe que Character/Scene utilisent
        push(k, url);
        // alias utile pour debug/interop
        push(`char_${k}`, url);
    }

    // 5) Objects → publier SOUS LA CLÉ DIRECTE idPhaser + alias "obj_<idPhaser>"
    for (const o of input.objects ?? []) {
        const k = normKey((o as any)?.idPhaser ?? (o as any)?.id ?? "");
        if (!k) continue;
        const url = pickUrl(o);
        if (!url) continue;

        // clé directe que GameObject/Scene utilisent
        push(k, url);
        // alias utile pour debug/interop
        push(`obj_${k}`, url);
    }

    return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
export function resolveAssetUrl(
    assets: TKeyValueDataString[],
    key: string
): string | undefined {
    const k = normKey(key);

    // 1) match exact
    const hit = assets.find(a => normKey(a.key) === k);
    if (hit?.value) return hit.value;

    // 2) si clé numérique → tenter sprite_/obstacle_/scene_
    if (isNum(k)) {
        const pref = [`sprite_${k}`, `obstacle_${k}`, `scene_${k}`];
        for (const p of pref) {
            const h = assets.find(a => normKey(a.key) === normKey(p));
            if (h?.value) return h.value;
        }
    }

    // 3) fallback pour entités runtime: essayer char_ / obj_
    for (const p of [`char_${k}`, `obj_${k}`]) {
        const h = assets.find(a => normKey(a.key) === normKey(p));
        if (h?.value) return h.value;
    }

    return undefined;
}

/** Fusionne deux listes de sons par 'name' (sans doublons) */
export function mergeSounds<
    T extends { name: string;[k: string]: any }
>(base: T[] = [], extra: T[] = []): T[] {
    const byName = new Map<string, T>();
    for (const s of base) byName.set(s.name, s);
    for (const s of extra) if (!byName.has(s.name)) byName.set(s.name, s);
    return Array.from(byName.values());
}
